import pick from "lodash/pick";
import { EventTypes } from "redux-segment";

import DataStorage from "utils/DataStorage";
import { convertToSnakeCase } from "utils/jsonFetch";

const userAttrs = [
  "id",
  "active",
  "email",
  "licenseNumber",
  "locale",
  "mobile",
  "phone",
  "roleInCompany",
  "dealColumns",
  "timezone",
  "title",
  "specialization",
  "hasGmailConnection",
  "invitationAccepted",
  "isSingleLandlordBroker",
  "createdAt",
  "emailConfirmedAt",
  "updatedAt",
];

const companyAttrs = [
  "id",
  "name",
  "address",
  "city",
  "country",
  "email",
  "phone",
  "locale",
  "role",
  "type",
  "createdAt",
  "currency",
  "period",
  "sizeUnit",
  "statsUnit",
  "dealDashboardUrl",
  "marketingDashboardUrl",
  "industries",
  "website",
  "logoUrl",
];

const removeSensitiveData = (user) =>
  convertToSnakeCase({
    ...pick(user, userAttrs),
    avatar: user.profilePictureUrl,
    company_id: user.company.id,
    company: pick(user.company, companyAttrs),
    name: user.fullName,
    userHash: user.hmacCode,
  });

const hideDefaultLauncher = (pathname) =>
  window.innerWidth < 1024 || (!!pathname && !!pathname.match(/flyers|companies/));

const segmentTracks = {
  skipDefaultMapping: true,
  mapper: {
    "@@router/LOCATION_CHANGE": (_, { payload: { pathname } }) => ({
      eventType: EventTypes.page,
      eventPayload: {
        options: {
          Intercom: {
            hideDefaultLauncher: hideDefaultLauncher(pathname),
          },
        },
      },
    }),

    USER_GET_FULFILLED: (_, { payload: { pathname, user } }) => {
      if (DataStorage.isImpersonating()) return null;

      return {
        eventType: EventTypes.identify,
        eventPayload: {
          avatar: user.profilePictureUrl,
          userId: user.id,
          userHash: user.hmacCode,
          traits: removeSensitiveData(user),
          options: {
            Intercom: {
              hideDefaultLauncher: hideDefaultLauncher(pathname),
            },
          },
        },
      };
    },
    USER_PASSWORD_RESET_FULFILLED: (getState) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "USER_PASSWORD_RESET",
        properties: {
          userId: getState().currentUser.id,
        },
      },
    }),
    USER_PASSWORD_RESET_REQUEST_FULFILLED: (getState) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "USER_PASSWORD_RESET_REQUEST",
        properties: {
          userId: getState().currentUser.id,
        },
      },
    }),
    USER_CONFIRM_FULFILLED: (_, { payload: { user } }) => [
      {
        eventType: EventTypes.track,
        eventPayload: {
          userId: user.id,
        },
      },
      {
        eventType: EventTypes.identify,
        eventPayload: {
          userId: user.id,
          userHash: user.hmacCode,
          traits: removeSensitiveData(user),
        },
      },
    ],
    USER_INVITE_FULFILLED: (getState, { meta, payload }) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "USER_REGISTER",
        properties: {
          userId: getState().currentUser.id,
          assetIds: meta.assetIDs,
          brokerId: payload.user.id,
        },
      },
    }),
    USER_REGISTER_FULFILLED: (_, action) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "USER_REGISTER",
        properties: {
          userId: action.payload.user.id,
        },
      },
    }),

    DEAL_CREATE_FULFILLED: (getState, action) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "DEAL_CREATE",
        properties: {
          userId: getState().currentUser.id,
          attributes: action.meta.attributes,
        },
      },
    }),
    DEAL_DISREGARD_FULFILLED: (getState, { payload }) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "DEAL_STATUS_UPDATE",
        properties: {
          userId: getState().currentUser.id,
          dealId: payload.deal.id,
          status: payload.deal.status,
        },
      },
    }),
    DEAL_CANCEL_FULFILLED: (getState, { payload }) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "DEAL_STATUS_UPDATE",
        properties: {
          userId: getState().currentUser.id,
          dealId: payload.deal.id,
          status: payload.deal.status,
        },
      },
    }),
    DEAL_REOPEN_FULFILLED: (getState, { payload }) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "DEAL_STATUS_UPDATE",
        properties: {
          userId: getState().currentUser.id,
          dealId: payload.deal.id,
          status: payload.deal.status,
        },
      },
    }),
    DEAL_EXECUTE_FULFILLED: (getState, { payload }) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "DEAL_STATUS_UPDATE",
        properties: {
          userId: getState().currentUser.id,
          dealId: payload.deal.id,
          status: payload.deal.status,
        },
      },
    }),

    DEALS_XLS_EXPORT_FULFILLED: (getState, action) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "DEALS_EXPORT",
        properties: {
          userId: getState().currentUser.id,
          portfolioId: action.meta.portfolioID,
          type: "xls",
        },
      },
    }),
    DEALS_PDF_EXPORT_FULFILLED: (getState, action) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "DEALS_EXPORT",
        properties: {
          userId: getState().currentUser.id,
          portfolioId: action.meta.portfolioID,
          type: "pdf",
        },
      },
    }),
    DEALS_FILTER: (getState, action) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "DEALS_FILTER",
        properties: {
          userId: getState().currentUser.id,
          ...action.meta.filters,
        },
      },
    }),

    POST_CREATE_FULFILLED: (getState, { meta }) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "POST_CREATE",
        properties: {
          userId: getState().currentUser.id,
          type: meta.type,
        },
      },
    }),
    PROPOSAL_CREATE_FULFILLED: (getState) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "POST_CREATE",
        properties: {
          userId: getState().currentUser.id,
          type: "Proposal",
        },
      },
    }),

    ASSETS_GET_FULFILLED: (getState, action) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "ASSETS_FILTER",
        properties: {
          userId: getState().currentUser.id,
          resultingAssetIds: action.payload.assets.map((a) => a.id),
          ...action.meta.filters,
        },
      },
    }),
    ASSETS_EXPORT_XLS_FULFILLED: (getState, action) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "ASSETS_EXPORT",
        properties: {
          userId: getState().currentUser.id,
          portfolioId: action.meta && action.meta.portfolioID,
          type: "xls",
        },
      },
    }),
    ASSETS_EXPORT_PDF_FULFILLED: (getState, action) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "ASSETS_EXPORT",
        properties: {
          userId: getState().currentUser.id,
          portfolioId: action.meta && action.meta.portfolioID,
          type: "pdf",
        },
      },
    }),

    ASSET_CREATE_FULFILLED: (getState, action) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "ASSET_CREATE",
        properties: {
          userId: getState().currentUser.id,
          assetId: action.payload.asset.id,
        },
      },
    }),
    ASSET_UPDATE_FULFILLED: (getState, action) => ({
      eventType: EventTypes.track,
      eventPayload: {
        properties: {
          event: "ASSET_UPDATE",
          userId: getState().currentUser.id,
          assetId: action.payload.asset.id,
        },
      },
    }),
    ASSET_FLYER_DOWNLOAD: (getState, action) => ({
      eventType: EventTypes.track,
      eventPayload: {
        properties: {
          userId: getState().currentUser.id,
          assetId: action.meta.assetID,
        },
      },
    }),
    ASSET_FLYER_OPEN: (getState, action) => ({
      eventType: EventTypes.track,
      eventPayload: {
        properties: {
          userId: getState().currentUser.id,
          assetId: action.meta.assetID,
        },
      },
    }),
    ASSET_EXPAND: (getState, action) => ({
      eventType: EventTypes.track,
      eventPayload: {
        properties: {
          userId: getState().currentUser.id,
          assetId: action.meta.assetID,
        },
      },
    }),
    ASSET_PHONE_REVEAL: (getState, action) => ({
      eventType: EventTypes.track,
      eventPayload: {
        properties: {
          userId: getState().currentUser.id,
          assetId: action.meta.assetID,
        },
      },
    }),
    ASSET_QUESTION_FULFILLED: (getState, action) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "ASSET_QUESTION",
        properties: {
          userId: getState().currentUser.id,
          assetID: action.meta.assetID,
        },
      },
    }),
    ASSET_ALL_SPACES_EXPAND: (getState) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "ASSET_ALL_SPACES_EXPAND",
        properties: {
          userId: getState().currentUser.id,
          portfolioIDs: getState().selectedPortfolioIDs,
        },
      },
    }),

    ASSET_PICTURE_CREATE_FULFILLED: (getState) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "ASSET_PICTURE_CREATE",
        properties: {
          userId: getState().currentUser.id,
          assetId: getState().asset.id,
        },
      },
    }),
    ASSET_ATTACHMENT_CREATE_FULFILLED: (getState) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "ASSET_ATTACHMENT_CREATE",
        properties: {
          userId: getState().currentUser.id,
          assetId: getState().asset.id,
        },
      },
    }),

    SPACE_CREATE_FULFILLED: (getState, action) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "SPACE_CREATE",
        properties: {
          userId: getState().currentUser.id,
          spaceId: action.payload.space.id,
        },
      },
    }),
    SPACE_UPDATE_FULFILLED: (getState, action) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "SPACE_UPDATE",
        properties: {
          userId: getState().currentUser.id,
          spaceId: action.payload.space.id,
        },
      },
    }),

    PORTFOLIOS_REQUEST_ACCESS_FULFILLED: (getState, action) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "PORTFOLIO_REQUEST_ACCESS",
        properties: {
          userId: getState().currentUser.id,
          portfolioId: action.meta.portfolioID,
        },
      },
    }),
    PORTFOLIO_SWITCH: (_, action) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "PORTFOLIO_SWITCH",
        properties: {
          portfolioIDs: action.payload.portfolioIDs,
        },
      },
    }),

    OAUTH_GMAIL_TOKEN_SAVE: (getState) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "GMAIL_ACCOUNT_CONNECTED",
        properties: {
          userId: getState().currentUser.id,
        },
      },
    }),

    COMPANY_ACCESS_REQUEST_CREATE_FULFILLED: (getState, action) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "COMPANY_ACCESS_REQUEST_CREATE",
        properties: {
          userId: getState().currentUser.id,
          companyId: action.meta.companyId,
        },
      },
    }),

    COMPANY_CREATE_FULFILLED: (getState, action) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "COMPANY_CREATE",
        properties: {
          userId: getState().currentUser.id,
          companyId: action.payload.companyId,
        },
      },
    }),
    COMPANY_SAVE_FULFILLED: (getState, { meta, payload }) => {
      if (meta.company.styleAttributes) {
        return {
          eventType: EventTypes.track,
          eventPayload: {
            event: "COMPANY_BRANDING_UPDATE",
            properties: {
              userId: getState().currentUser.id,
              companyId: payload.companyId,
            },
          },
        };
      }

      return null;
    },

    COMPANY_USER_CREATE_FULFILLED: (getState, { payload }) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "COMPANY_USER_CREATE",
        properties: {
          userId: getState().currentUser.id,
          createdUserId: payload.user.id,
        },
      },
    }),

    FLOORPLAN_CREATE_FULFILLED: (getState, { payload }) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "FLOORPLAN_CREATE",
        properties: {
          userId: getState().currentUser.id,
          id: payload.floorplan.id,
        },
      },
    }),

    FLYER_CONTACT_FULFILLED: (getState) => ({
      eventType: EventTypes.track,
      eventPayload: {
        event: "FLYER_CONTACT",
        properties: {
          flyerId: getState().flyer.id,
        },
      },
    }),
  },
};

export default segmentTracks;
